import { useCreateClient, useUpdateClient } from "@/services/clients/mutation";
import { Client } from "@/types/client";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  InputRef,
  message,
  Modal,
  Radio,
  Select,
  Space,
} from "antd";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import EmailFormItem from "../../form/items/EmailFormItem";
import PhoneFormItem from "../../form/items/PhoneFormItem";
import RutFormItem from "../../form/items/RutFormItem";

export interface ModalCUClientRef {
  childFunction: (register?: Client) => void;
}

export const ModalCUClient = forwardRef<ModalCUClientRef>((props, ref) => {
  const [open, setOpen] = useState<boolean>(false);
  const [id, setId] = useState<string>("");
  const [isEdit, setIsEdit] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const customer_type_options = [
    {
      label: "Persona",
      value: "person",
    },
    {
      label: "Empresa",
      value: "company",
    },
  ];

  const firstInputRef = useRef<InputRef | null>(null);

  const [form] = Form.useForm();

  const [alert, setAlert] = useState({
    visible: false,
    description: "",
  });

  const childFunction = (register?: Client) => {
    setOpen(true);
    setAlert({ visible: false, description: "" });
    setIsEdit(false);

    if (register) {
      //si viene id es edit
      setId(register.id);
      setIsEdit(true);
      form.setFieldsValue({
        type: register.type,
        tax_id: register.tax_id ?? (register as any).tax_id,
        name: register.name,
        email: register.email,
        phone: register.phone,
        discount_percent:
          register.discount_percent ?? (register as any).discount_percent,
        credit_enabled:
          register.credit_enabled ?? (register as any).credit_enabled,
        credit_limit: register.credit_limit ?? (register as any).credit_limit,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        type: "person",
        credit_enabled: false,
      });
    }
  };

  useImperativeHandle(ref, () => ({
    childFunction,
  }));

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 300);
    }
  }, [open]);

  const createClientMutation = useCreateClient();
  const updateClientMutation = useUpdateClient();

  const handleError = (error: any) => {
    console.log(error);

    let error_message = "Ups, algo salió mal. Intenta nuevamente.";

    if (typeof error?.response?.data?.message === "string") {
      error_message = error.response.data.message;
    }

    messageApi.error(error_message);

    if (error?.response?.data?.success === false) {
      setAlert({
        visible: true,
        description: error_message,
      });
    }
  };

  const manage = async () => {
    setIsLoadingButton(true);
    setAlert({ visible: false, description: "" });

    const formValues = form.getFieldsValue() as Client;

    if (isEdit) {
      formValues.id = id;
    }

    const mutation = isEdit ? updateClientMutation : createClientMutation;

    mutation.mutate(formValues, {
      onSuccess: () => {
        messageApi.success("Cliente guardado correctamente.");

        if (!isEdit) {
          form.resetFields();
        }

        setOpen(false);
      },
      onError: handleError,
      onSettled: () => {
        setIsLoadingButton(false);
      },
    });
  };

  return (
    <Modal
      title={isEdit ? "Editar" : "Agregar"}
      centered
      open={open}
      width={600}
      onCancel={() => setOpen(false)}
      footer={null}
    >
      {contextHolder}

      <Form
        form={form}
        scrollToFirstError={{
          behavior: "smooth",
          block: "center",
          inline: "center",
        }}
        layout="vertical"
        onFinish={manage}
      >
        <div className="grid grid-cols-1 sm:gap-4 sm:grid-cols-2">
          <Form.Item
            name="type"
            label="Tipo de cliente"
            rules={[
              {
                required: true,
                message: "Debe seleccionar un tipo de cliente",
              },
            ]}
          >
            <Select
              placeholder="Selecciona tipo de cliente"
              options={customer_type_options}
            />
          </Form.Item>

          <RutFormItem
            name={"tax_id"}
            form={form}
            required={false}
            ref={firstInputRef}
            label="RUN"
          />
        </div>

        {/* Name */}
        <Form.Item
          name="name"
          validateTrigger="onBlur"
          label="Nombre"
          rules={[{ required: true, max: 20, whitespace: true, min: 2 }]}
        >
          <Input placeholder="Ingresa nombre" />
        </Form.Item>

        <div className="grid grid-cols-1 sm:gap-4 sm:grid-cols-2">
          {/* email */}
          <EmailFormItem />
          <PhoneFormItem />
        </div>

        <Form.Item name="discount_percent" label="Porcentaje de descuento">
          <InputNumber
            min={0}
            max={100}
            controls={false}
            addonBefore="%"
            className="w-full"
            rootClassName="w-full"
            style={{ width: "100%" }}
            placeholder="Ingresa porcentaje"
            formatter={(value) => `${value}`.replace(/[^\d]/g, "")}
            parser={(value: any) => value.replace(/[^\d]/g, "")}
          />
        </Form.Item>

        {/* Aplicar crédito exclusivo */}
        <Form.Item
          name="credit_enabled"
          label="¿Aplicar crédito exclusivo?"
          tooltip="El monto ingresado será utilizado como límite de crédito para el cliente."
        >
          <Radio.Group className="font-normal">
            <Radio className="font-normal" value={true}>
              Sí
            </Radio>

            <Radio className="font-normal" value={false}>
              No
            </Radio>
          </Radio.Group>
        </Form.Item>

        {/* Monto crédito */}
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.credit_enabled !== currentValues.credit_enabled
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("credit_enabled") === true && (
              <Form.Item
                name="credit_limit"
                label="Monto límite"
                validateTrigger="onBlur"
              >
                <InputNumber
                  min={0}
                  max={999999999}
                  controls={false}
                  style={{ width: "100%" }}
                  placeholder="Ingresa monto límite"
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                  }
                  parser={(value: any) => value.replace(/\./g, "")}
                />
              </Form.Item>
            )
          }
        </Form.Item>

        {alert.visible && (
          <div className="mb-4">
            <Alert
              message="Error"
              description={alert.description}
              type="error"
              showIcon
            />
          </div>
        )}

        {/* Button */}
        <Form.Item className="flex justify-end items-end">
          <Space size="middle">
            <Button danger type="text" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" loading={isLoadingButton}>
              Guardar
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
});
